/**
 * Full-screen maintenance gate for an app that is already open.
 *
 * The Node server answers page loads with the maintenance page while the site
 * is closed, but a tab opened before that keeps running the app, and every
 * call it makes fails. This checks the status endpoint on a timer (and right
 * away when the axios interceptor sees the maintenance 503), swaps the whole
 * app for a maintenance screen while the site is closed, and reloads the page
 * once it reopens.
 *
 * Unmounting the app while closed is deliberate: it stops the API calls,
 * websockets and timers that would otherwise keep failing.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as constants from "Constants";
import { getAuthorizationHeader } from "services/httpService";
import { setTelemetryPaused } from "services/telemetry";
import { maintenanceStatusReceived } from "Store/Maintenance/Actions";
import { formatRemaining, nextPollDelay, shouldShowGate } from "Store/Maintenance/utils";

const STATUS_URL = `${constants.base_url}/api/platform-settings/maintenance/`;
const BYPASS_COOKIE = "bt_maint_bypass";
const DEFAULT_MESSAGE = "The site is briefly unavailable while we carry out maintenance.";
/** Pages that stay usable while closed, so staff can sign in. */
const STAFF_PATHS = ["/login"];

export async function fetchMaintenanceStatus(fetchImpl = fetch) {
  // Plain fetch, not httpService: a failed check must not run through the
  // interceptor that reports errors and detects maintenance.
  const headers = { Accept: "application/json" };
  const authorization = getAuthorizationHeader();
  if (authorization) headers.Authorization = authorization;
  const response = await fetchImpl(STATUS_URL, { headers, cache: "no-store" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  if (typeof data?.active !== "boolean") throw new Error("Unexpected maintenance status");
  return data;
}

const styles = {
  page: {
    position: "fixed",
    inset: 0,
    zIndex: 2147483000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    textAlign: "center",
    fontFamily: "Arial, sans-serif",
  },
  card: {
    maxWidth: "600px",
    padding: "40px",
    borderRadius: "20px",
    background: "rgba(255, 255, 255, 0.1)",
    boxShadow: "0 8px 32px rgba(31, 38, 135, 0.37)",
  },
  message: { fontSize: "1.2rem", lineHeight: 1.6, margin: "20px 0" },
  small: { opacity: 0.8, fontSize: "0.9rem" },
  button: {
    marginTop: "16px",
    padding: "8px 20px",
    borderRadius: "6px",
    border: "1px solid #fff",
    background: "transparent",
    color: "#fff",
    cursor: "pointer",
  },
  link: { color: "#fff", opacity: 0.8, fontSize: "0.85rem" },
};

function MaintenanceScreen({ maintenance, checking, onCheck }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const end = maintenance.expectedEnd ? new Date(maintenance.expectedEnd) : null;
  const hasEnd = end && !Number.isNaN(end.getTime());
  const remaining = hasEnd ? end.getTime() - (now + (maintenance.serverOffsetMs || 0)) : null;

  return (
    <div role="alert" aria-live="polite" style={styles.page}>
      <div style={styles.card}>
        <h1>Site Under Maintenance</h1>
        <p style={styles.message}>{maintenance.messageText || DEFAULT_MESSAGE}</p>
        {hasEnd && (
          <p>
            Expected back by{" "}
            <strong>{end.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</strong>{" "}
            (your time)
            {remaining > 0 ? `, about ${formatRemaining(remaining)} from now` : ""}.
          </p>
        )}
        <p style={styles.small}>This page checks automatically and reloads when the site is back.</p>
        <button type="button" style={styles.button} onClick={onCheck} disabled={checking}>
          {checking ? "Checking…" : "Check now"}
        </button>
        <p style={{ marginTop: "24px" }}>
          <a style={styles.link} href="/login?maintenance_bypass=1">
            Staff sign in
          </a>
        </p>
      </div>
    </div>
  );
}

export default function MaintenanceGate({ children }) {
  const dispatch = useDispatch();
  const maintenance = useSelector((state) => state.maintenance);
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState(0);
  const timer = useRef(null);
  const reloadScheduled = useRef(false);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      dispatch(maintenanceStatusReceived(await fetchMaintenanceStatus()));
    } catch (e) {
      // A failed check changes nothing: it must neither close an open site
      // nor reopen a closed one.
    } finally {
      setChecking(false);
      setChecks((n) => n + 1);
    }
  }, [dispatch]);

  // First check on load, and again as soon as an API call hits the gate.
  useEffect(() => {
    check();
  }, [check, maintenance.detectedAt]);

  // The next check, rescheduled after every check and whenever the state or
  // the tab's visibility changes. Hidden tabs do not poll.
  useEffect(() => {
    const schedule = () => {
      clearTimeout(timer.current);
      const delay = nextPollDelay(maintenance, { hidden: document.hidden });
      if (delay != null) timer.current = setTimeout(check, delay);
    };
    const onVisibility = () => (document.hidden ? clearTimeout(timer.current) : check());
    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", check);
    return () => {
      clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", check);
    };
  }, [maintenance, checks, check]);

  const closed = shouldShowGate(maintenance);

  // Every call fails while closed, by design; do not report each one.
  useEffect(() => {
    setTelemetryPaused(closed);
  }, [closed]);

  // Let staff who may use the site reload it past the Node gate.
  useEffect(() => {
    if (maintenance.active && maintenance.bypass) {
      document.cookie = `${BYPASS_COOKIE}=1; path=/; max-age=3600; SameSite=Lax`;
    }
  }, [maintenance.active, maintenance.bypass]);

  // Reopened after being shown: reload into a fresh app. The random delay
  // outlasts the Node gate's cache and spreads everyone's return out.
  useEffect(() => {
    if (maintenance.shown && !closed && !reloadScheduled.current) {
      reloadScheduled.current = true;
      setTimeout(() => window.location.reload(), 5000 + Math.random() * 5000);
    }
  }, [maintenance.shown, closed]);

  const onStaffPage = STAFF_PATHS.some((p) => window.location.pathname.startsWith(p));
  if (closed && !onStaffPage) {
    return <MaintenanceScreen maintenance={maintenance} checking={checking} onCheck={check} />;
  }
  return children;
}
